'use client'

import { Suspense, useActionState, useEffect, useMemo } from 'react'
import { useSearchParams, useRouter } from 'next/navigation'
import { ContentWrapper } from '@/General/ContentWrapper'
import { H1 } from '@/General/H1'
import { Input } from '@/General/Forms/Input'
import { SubmitButton } from '@/MainLayout/Header/TopMenu/LoginModal/SubmitButton'
import { ModalHeader } from '@/MainLayout/Header/TopMenu/LoginModal/ModalHeader'
import { validateInputs } from '@/MainLayout/Header/TopMenu/LoginModal/helpers'
import { useAuth } from '@/Auth/useAuth'
import { TextLink } from '@/General/TextLink'
import { LoadingSpinner } from '@/General/LoadingSpinner'
import { sanitizeRedirectPath } from '@/lib/auth/safeRedirect'

const LoginPageContent = () => {
  const searchParams = useSearchParams()
  const router = useRouter()
  const { signIn, isAuthenticated, isResolved } = useAuth()
  const redirectTo = useMemo(() => {
    const rawRedirect = searchParams.get('redirect') || searchParams.get('redirectTo')
    return sanitizeRedirectPath(rawRedirect, '/account/')
  }, [searchParams])
  const redirectTarget = redirectTo

  useEffect(() => {
    if (isResolved && isAuthenticated) {
      router.replace(redirectTarget)
    }
  }, [router, isAuthenticated, isResolved, redirectTarget])

  const [state, submitAction, isPending] = useActionState(
    async (_: unknown, formData: FormData) => {
      const data = {
        inputs: {
          email: formData.get('email') as string,
          password: formData.get('password') as string,
        },
      }

      const validation = validateInputs(data)
      if (!validation.success) {
        return { errors: validation.errors, data }
      }

      try {
        const success = await signIn(data.inputs.email, data.inputs.password, {
          context: 'account-login',
          redirectTo: redirectTarget,
        })

        if (success) {
          return
        }
      } catch {
        return {
          errors: [{ key: null, value: 'Login failed. Please try again.' }],
          data: {
            inputs: {
              email: data.inputs.email,
              password: '',
            },
          },
        }
      }
    },
    null
  )

  const signupParams = new URLSearchParams()
  if (redirectTo && redirectTo !== '/account/') {
    signupParams.set('redirect', redirectTo)
  }
  const signupUrl = `/account/signup${
    signupParams.toString() ? `?${signupParams.toString()}` : ''
  }`

  if (!isResolved || isAuthenticated) {
    return (
      <ContentWrapper>
        <div className="flex flex-col items-center py-16">
          <H1>Log in to your account</H1>
          <LoadingSpinner />
        </div>
      </ContentWrapper>
    )
  }

  return (
    <ContentWrapper>
      <div className="max-w-md mx-auto bg-white border border-gray-200 rounded-lg shadow-sm">
        <div className="px-6 py-5 border-b border-gray-200">
          <ModalHeader />
        </div>
        <div className="px-6 py-6">
          <H1>Log in to your account</H1>
          <p className="text-gray-600 mt-2">
            Please log in to continue to the page you were on.
          </p>

          <form action={submitAction} className="space-y-4 mt-6" noValidate>
            {state?.errors?.length != null && state.errors.length > 0 && (
              <div className="text-red-700 text-sm text-center px-3 py-2 bg-red-50 rounded-md">
                {state.errors.map(error => error.value).join(', ')}
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-semibold leading-6 text-gray-900 mb-2"
                >
                  Email address
                </label>
                <Input
                  key={state?.data?.inputs?.email ?? 'email'}
                  defaultValue={state?.data?.inputs?.email}
                  isError={state?.errors?.some(error => error.key === 'email')}
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  placeholder="Enter your email"
                />
              </div>

              <div>
                <label
                  htmlFor="password"
                  className="block text-sm font-semibold leading-6 text-gray-900 mb-2"
                >
                  Password
                </label>
                <Input
                  key={state?.data?.inputs?.password ?? 'password'}
                  defaultValue={state?.data?.inputs?.password}
                  isError={state?.errors?.some(
                    error => error.key === 'password'
                  )}
                  id="password"
                  name="password"
                  type="password"
                  placeholder="Enter your password"
                />
              </div>
            </div>
            <div className="pt-1">
              <SubmitButton isPending={isPending} />
            </div>
          </form>

          <div className="mt-6 pt-5 border-t border-gray-200 text-center">
            <p className="text-sm text-gray-600">
              Don&apos;t have an account?{' '}
              <TextLink href={signupUrl}>Sign up for free</TextLink>
              {' · '}
              <TextLink href="/account/reset/">Forgot password?</TextLink>
            </p>
          </div>
        </div>
      </div>
    </ContentWrapper>
  )
}

export default function LoginPage() {
  return (
    <Suspense
      fallback={
        <ContentWrapper>
          <div className="text-center py-8">
            <H1>Log in to your account</H1>
            <LoadingSpinner />
          </div>
        </ContentWrapper>
      }
    >
      <LoginPageContent />
    </Suspense>
  )
}
